import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Person,
  Phone,
  Email,
  Save,
  CheckCircle
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface ProfileData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  timezone: string;
}

const ProfileSettings: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    timezone: 'Asia/Kolkata'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProfile();
      if (response.user) {
        setProfile({
          firstName: response.user.firstName || '',
          lastName: response.user.lastName || '',
          phone: response.user.phone || '',
          email: response.user.email || '',
          timezone: response.user.timezone || 'Asia/Kolkata'
        });
      }
    } catch (err: any) {
      setError('Failed to load profile');
      console.error('Load profile error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof ProfileData, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await apiService.updateProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        timezone: profile.timezone
      });

      if (response.success) {
        setSuccess('Profile updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
      console.error('Save profile error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Person color="primary" />
        Profile Settings
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Email (read-only) */}
        <TextField
          label="Email"
          value={profile.email}
          fullWidth
          disabled
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email color="action" />
              </InputAdornment>
            ),
          }}
          helperText="Email cannot be changed"
        />

        {/* Name fields */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="First Name"
            value={profile.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={profile.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            fullWidth
          />
        </Box>

        {/* Phone Number */}
        <TextField
          label="Phone Number (for WhatsApp)"
          value={profile.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          fullWidth
          placeholder="+919876543210"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone color="action" />
              </InputAdornment>
            ),
          }}
          helperText="Include country code (e.g., +91 for India). Required for WhatsApp notifications."
        />

        <Divider sx={{ my: 2 }} />

        {/* Info Box */}
        <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.dark">
            <strong>Why phone number?</strong>
            <br />
            Your phone number is used to send WhatsApp reminders and notifications. 
            Make sure to use the number registered with WhatsApp.
          </Typography>
        </Box>

        {/* Save Button */}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          fullWidth
          size="large"
        >
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </Box>
    </Paper>
  );
};

export default ProfileSettings;
